import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useGridApiRef } from "@mui/x-data-grid";
import { csCZ } from "@mui/x-data-grid/locales";
import { useBackend } from "../../hooks";
import { BackendFeedback } from "../index";
import AsyncDataGridToolbar from "./AsyncDataGridToolbar.jsx";
import AsyncDataGridView from "./AsyncDataGridView.jsx";
import { useAsyncDataGridState } from "./useAsyncDataGridState.js";

/**
 * AsyncDataGrid component - A wrapper around MUI DataGrid with built-in async data loading
 *
 * @param {Object} props
 * @param {Function} props.fetchData - Function to fetch data (client-side mode)
 * @param {Function} props.dataTransform - Function to transform fetched data
 * @param {string} props.mode - Loading mode: "client" or "server"
 * @param {Object} props.dataSource - Data source for server-side mode
 * @param {Array|Function} props.columns - Column definitions for the grid, or a factory function receiving { reload } that returns columns
 * @param {Function} props.getRowId - Function to get unique row ID
 * @param {Object} props.toolbarProps - Props for the toolbar
 * @param {import("../../../config/CsvExportConfigs.js").CsvExportConfig} props.exportConfig - CSV/JSON export configuration
 * @param {boolean} [props.hideExport] - Hide print and export controls in the toolbar
 * @param {number} props.defaultPageSize - Default page size
 * @param {Array} props.pageSizeOptions - Available page size options
 * @param {number|string} props.height - Height of the grid (ignored when autoHeight is true)
 * @param {boolean} [props.autoHeight] - When true, grid grows with rows and page scrolls
 * @param {Function} props.translateError - Function to translate error messages
 * @param {Function} [props.onDataLoad] - Optional callback invoked with transformed rows when data is loaded (client mode only)
 * @param {string} [props.urlStateKey] - Short prefix for URL persistence ({@link import("../../../config/DataGridUrlStateKeys.js").default})
 * @param {boolean} [props.hidePresets] - Hide preset toolbar controls
 */
AsyncDataGrid.propTypes = {
  fetchData: PropTypes.func,
  dataTransform: PropTypes.func,
  mode: PropTypes.oneOf(["client", "server"]),
  dataSource: PropTypes.object,
  onFilterModelChange: PropTypes.func,
  onSortModelChange: PropTypes.func,
  onPaginationModelChange: PropTypes.func,
  onColumnVisibilityModelChange: PropTypes.func,
  columns: PropTypes.oneOfType([PropTypes.array, PropTypes.function]).isRequired,
  getRowId: PropTypes.func,
  toolbarProps: PropTypes.object,
  exportConfig: PropTypes.shape({
    filename: PropTypes.string.isRequired,
    exportAll: PropTypes.bool,
    columns: PropTypes.arrayOf(
      PropTypes.shape({
        field: PropTypes.string.isRequired,
        path: PropTypes.string,
        header: PropTypes.string,
      }),
    ),
  }).isRequired,
  hideExport: PropTypes.bool,
  defaultPageSize: PropTypes.number,
  pageSizeOptions: PropTypes.array,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  autoHeight: PropTypes.bool,
  translateError: PropTypes.func,
  onDataLoad: PropTypes.func,
  urlStateKey: PropTypes.string,
  hidePresets: PropTypes.bool,
};

function AsyncDataGrid({
  fetchData,
  dataTransform,
  mode = "client",
  dataSource,
  onFilterModelChange,
  onSortModelChange,
  onPaginationModelChange,
  onColumnVisibilityModelChange,
  columns,
  getRowId,
  toolbarProps = {},
  exportConfig,
  hideExport = false,
  defaultPageSize = 25,
  pageSizeOptions = [25, 50, 100],
  height = 600,
  autoHeight = false,
  translateError,
  onDataLoad,
  urlStateKey,
  hidePresets = false,
  slots = {},
  slotProps = {},
  apiRef: apiRefProp = null,
  initialState: userInitialState,
  ...dataGridProps
}) {
  const [reloadTrigger, setReloadTrigger] = React.useState(0);
  const internalApiRef = useGridApiRef();
  const gridApiRef = apiRefProp ?? internalApiRef;

  const reload = React.useCallback(() => {
    setReloadTrigger((prev) => prev + 1);
  }, []);

  // Re-attach after every commit — gridApiRef.current is populated only once DataGrid mounts,
  // which can happen after the first effect run (e.g. BackendFeedback silentPending cycle).
  React.useLayoutEffect(() => {
    if (gridApiRef.current) {
      gridApiRef.current.reload = reload;
    }
  });

  const backendCall = useBackend(fetchData, [reloadTrigger], mode === "client");

  const resolvedColumns = useMemo(() => {
    if (typeof columns === "function") {
      return columns({ reload });
    }
    return columns;
  }, [columns, reload]);

  const columnFields = useMemo(() => resolvedColumns.map((column) => column.field).filter(Boolean), [resolvedColumns]);

  const { urlStateBindings, presetsBindings } = useAsyncDataGridState({
    urlStateKey,
    hidePresets,
    defaultPageSize,
    initialState: userInitialState,
    columnFields,
    onFilterModelChange,
    onSortModelChange,
    onPaginationModelChange,
    onColumnVisibilityModelChange,
  });

  const gridSlots = useMemo(
    () => ({
      toolbar: AsyncDataGridToolbar,
      ...slots,
    }),
    [slots],
  );

  const gridSlotProps = useMemo(() => {
    const userToolbar = slotProps.toolbar ?? {};
    return {
      ...slotProps,
      toolbar: {
        quickFilterProps: { debounceMs: 300 },
        ...toolbarProps,
        ...userToolbar,
        csvOptions: {
          ...toolbarProps?.csvOptions,
          ...userToolbar.csvOptions,
          disableToolbarButton: true,
        },
      },
    };
  }, [toolbarProps, slotProps]);

  React.useEffect(() => {
    if (mode === "client" && onDataLoad && backendCall.feedback !== "pending" && backendCall.data) {
      const rows = dataTransform ? dataTransform(backendCall.data) : backendCall.data;
      onDataLoad(rows);
    }
  }, [mode, onDataLoad, backendCall.feedback, backendCall.data, dataTransform]);

  const commonDataGridProps = {
    localeText: csCZ.components.MuiDataGrid.defaultProps.localeText,
    columns: resolvedColumns,
    getRowId,
    pagination: true,
    pageSizeOptions,
    slots: gridSlots,
    slotProps: gridSlotProps,
    showToolbar: true,
    ignoreDiacritics: true,
    ...(autoHeight ? { autoHeight: true } : {}),
    ...dataGridProps,
    apiRef: gridApiRef,
  };

  const gridWrapperSx = autoHeight ? { width: "100%" } : { height, width: "100%" };

  const gridChangeHandlers = {
    onFilterModelChange,
    onSortModelChange,
    onPaginationModelChange,
    onColumnVisibilityModelChange,
  };

  const sharedViewProps = {
    gridWrapperSx,
    exportConfig,
    gridColumns: resolvedColumns,
    hideExport,
    commonDataGridProps,
    userInitialState,
    defaultPageSize,
    urlStateBindings,
    gridChangeHandlers,
    presetsBindings,
  };

  if (mode === "server") {
    if (!dataSource) {
      console.error("AsyncDataGrid: dataSource is required when mode='server'");
      return null;
    }

    return (
      <AsyncDataGridView
        {...sharedViewProps}
        exportRows={[]}
        exportLoading={false}
        isServerMode
        dataGridModeProps={{ dataSource }}
      />
    );
  }

  function renderGrid({ data, feedback }) {
    const isLoading = feedback === "pending";
    const rows = data ? (dataTransform ? dataTransform(data) : data) : [];

    return (
      <AsyncDataGridView
        {...sharedViewProps}
        exportRows={rows}
        exportLoading={isLoading}
        isServerMode={false}
        dataGridModeProps={{ rows, loading: isLoading }}
      />
    );
  }

  return (
    <BackendFeedback backendCall={backendCall} translateError={translateError} silentPending>
      {renderGrid}
    </BackendFeedback>
  );
}

export default AsyncDataGrid;
